import { createSlug } from '../../util/urlHelpers';

const toIsoDate = date => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Adapters from listing-shaped entities to the plain data buildProfileSchema expects.
// Both the SSR duck and the client component use these so the two schemas stay identical.
export const serviceListingSchemaData = (listing, canonicalRootURL) => ({
  url: `${canonicalRootURL}/l/${createSlug(listing.attributes?.title || '')}/${listing.id.uuid}`,
  name: listing.attributes?.title,
  description: listing.attributes?.description,
});

export const portfolioWorkSchemaData = (work, canonicalRootURL, imageUrl) => ({
  url: `${canonicalRootURL}/work/${createSlug(work.attributes?.title || '')}/${work.id.uuid}`,
  name: work.attributes?.title,
  description: work.attributes?.description || work.attributes?.title,
  imageUrl,
});

/**
 * knowsAbout labels for the Person node: the user's skill and tool keys
 * translated through the marketplace skill/tool options — the same labels the
 * profile pills and the skill SEO page ItemList show. Keys without a
 * translation are dropped.
 */
export const knowsAboutFromPublicData = (
  publicData = {},
  skillOptions = [],
  toolOptions = [],
  locale
) => {
  const translationField = locale === 'fi' ? 'fiTranslation' : 'enTranslation';
  // Same key sources and legacy fallbacks as FreelancerAboutMeContainer /
  // buildAuthorPublicData: experienceKeys (fallback experience[].category)
  // and toolsKeys (fallback tools[].category).
  const skillKeys =
    Array.isArray(publicData.experienceKeys) && publicData.experienceKeys.length > 0
      ? publicData.experienceKeys
      : (publicData.experience || []).map(e => e?.category).filter(Boolean);
  const toolKeys =
    Array.isArray(publicData.toolsKeys) && publicData.toolsKeys.length > 0
      ? publicData.toolsKeys
      : (publicData.tools || []).map(t => t?.category).filter(Boolean);
  const options = [...skillOptions, ...toolOptions];
  return [...skillKeys, ...toolKeys]
    .map(key => options.find(o => o.key === key)?.[translationField])
    .filter(Boolean);
};

/**
 * Build the ProfilePage JSON-LD schema from plain data, so the same code
 * runs server-side (ProfilePage.duck.js loadData) and client-side
 * (ProfilePage.js fallback for client-side navigation).
 *
 * reviews:  [{ authorName, rating, content, createdAt }]
 * services: [{ url, name, description }]
 * works:    [{ url, name, description, imageUrl }]
 */
export const buildProfileSchema = ({
  profileUrl,
  schemaTitle,
  displayName,
  whoAmI,
  knowsAbout = [],
  bio,
  locationCity,
  locationCountryCode,
  profileImageUrl,
  createdAt,
  reviews = [],
  services = [],
  works = [],
}) => {
  const schemaReviews = reviews.map(review => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.authorName,
    },
    datePublished: toIsoDate(review.createdAt),
    reviewBody: review.content,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: '5',
      worstRating: '1',
    },
  }));

  const averageRating =
    reviews.length > 0
      ? Math.round((reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length) * 10) /
        10
      : 0;

  const serviceSchemas = services.map(service => ({
    '@type': 'Service',
    '@id': service.url,
    name: service.name,
    ...(service.description && { description: service.description }),
    url: service.url,
    provider: {
      '@type': 'Person',
      name: displayName,
    },
  }));

  const workSchemas = works.map(work => ({
    '@type': 'CreativeWork',
    '@id': work.url,
    name: work.name,
    ...(work.description && { description: work.description }),
    url: work.url,
    author: {
      '@type': 'Person',
      name: displayName,
    },
    ...(work.imageUrl && { image: work.imageUrl }),
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': profileUrl,
    name: schemaTitle,
    url: profileUrl,
    ...(createdAt && { dateCreated: toIsoDate(createdAt) }),
    ...(schemaReviews.length > 0 && { review: schemaReviews }),
    ...(reviews.length > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: averageRating,
        reviewCount: reviews.length,
        bestRating: '5',
        worstRating: '1',
      },
    }),
    mainEntity: {
      '@type': 'Person',
      '@id': `${profileUrl}#person`,
      name: displayName,
      url: profileUrl,
      ...(profileImageUrl && { image: profileImageUrl }),
      ...(whoAmI && { jobTitle: whoAmI }),
      ...(knowsAbout.length > 0 && { knowsAbout }),
      ...(bio && { description: bio }),
      ...(locationCity && {
        address: {
          '@type': 'PostalAddress',
          addressLocality: locationCity,
          ...(locationCountryCode && { addressCountry: locationCountryCode }),
        },
      }),
      ...(serviceSchemas.length > 0 && { makesOffer: serviceSchemas }),
      ...(workSchemas.length > 0 && { workExample: workSchemas }),
    },
  };
};
